/**
 * TrialFeatureBanner
 *
 * Displays a sticky amber banner at the top of a page/section when the user
 * is in an active trial AND the given feature is NOT part of their actual plan
 * (i.e., it's available only because of the trial).
 *
 * Usage:
 *   <TrialFeatureBanner featureKey="sales.enabled" />
 *
 * Renders nothing when:
 *   - Not in trial
 *   - Feature is part of the plan (not trial-only)
 *   - User is SuperAdmin
 */
import { useAuth } from "@/contexts/AuthContext";
import { Clock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { billingPath } from "@/utils/accountRoutes";

export default function TrialFeatureBanner({ featureKey }) {
  const {
    isTrial,
    trialDaysRemaining,
    isTrialOnlyFeature,
    isSuperAdmin,
    isAdmin,
    user,
  } = useAuth();
  const navigate = useNavigate();

  // SuperAdmins never see the banner
  if (isSuperAdmin()) return null;

  // Only show during active trial for features not in the plan
  if (!isTrial || !isTrialOnlyFeature(featureKey)) return null;

  const daysText =
    trialDaysRemaining === 1
      ? "1 dia"
      : `${trialDaysRemaining} dias`;

  const clientId = user?.accountId;

  return (
    <div className="w-full bg-gradient-to-r from-amber-500/15 via-orange-500/15 to-amber-500/15 border border-amber-500/30 rounded-lg px-4 py-3 mb-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
            <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <p className="text-sm text-amber-800 dark:text-amber-200 leading-snug">
            Este é um <strong>recurso pago</strong> e está disponível por mais{" "}
            <strong>{daysText}</strong> durante seu período de avaliação.
          </p>
        </div>

        {isAdmin() && clientId && (
          <button
            type="button"
            onClick={() => navigate(billingPath(clientId))}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-amber-900 dark:text-amber-100 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 rounded-lg transition-colors"
          >
            Ver planos
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
